import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Empty, message, notification } from 'antd';
import dayjs from 'dayjs';

import { EMPTY_MESSAGES, NOTIFICATION_CONFIG } from '@/shared/lib/constants';
import { experimentKeys } from '@/shared/lib/query/queryKeys';
import { sortTagsSafe } from '@/shared/lib/sort';
import type { DatasetWithTitle, FeatureSetWithTitle, ParameterSetWithTitle, WorkbenchAssets } from '@/shared/types';
import type { BadgeItem } from '@/shared/types/badge';
import { BadgeList } from '@/shared/ui/BadgeList';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import type { AssetSearchResultData } from '@/shared/ui/InfiniteAssetSelect';
import { InfiniteAssetSelect } from '@/shared/ui/InfiniteAssetSelect';

import { fetchAssetDetail } from '@/entities/asset-search/api/assetDetailApi';
import { updateRunTags } from '@/entities/experiment/api/experimentTagsApi';

import { ExperimentTagsEditModal } from '@/widgets/experiment-asset-detail/ExperimentTagsEditModal';

import styles from './WorkbenchSidebar.module.css';

type WorkbenchSidebarProps = {
  initialFeatureSet?: FeatureSetWithTitle;
  initialParameterSet?: ParameterSetWithTitle;
  initialDataset?: DatasetWithTitle;
  onAssetChange?: (assets: WorkbenchAssets) => void;
  runId?: number;
  experimentTags?: string[];
};

export const WorkbenchSidebar: React.FC<WorkbenchSidebarProps> = ({
  initialFeatureSet,
  initialParameterSet,
  initialDataset,
  onAssetChange,
  runId,
  experimentTags,
}) => {
  const [featureSet, setFeatureSet] = useState<FeatureSetWithTitle | undefined>(initialFeatureSet);
  const [parameterSet, setParameterSet] = useState<ParameterSetWithTitle | undefined>(initialParameterSet);
  const [dataset, setDataset] = useState<DatasetWithTitle | undefined>(initialDataset);

  const [hasChangedFeatureSet, setHasChangedFeatureSet] = useState(false);
  const [hasChangedParameterSet, setHasChangedParameterSet] = useState(false);
  const [hasChangedDataset, setHasChangedDataset] = useState(false);

  const [showAssetChangeWarning, setShowAssetChangeWarning] = useState(false);
  const [hasShownAssetChangeWarning, setHasShownAssetChangeWarning] = useState(false);
  const [pendingAssetChange, setPendingAssetChange] = useState<{
    assetType: 'dataset' | 'feature_set' | 'parameter_set';
    data: AssetSearchResultData;
  } | null>(null);

  const [isEditingTags, setIsEditingTags] = useState(false);
  const queryClient = useQueryClient();

  const handleAssetChange = async (
    assetType: 'dataset' | 'feature_set' | 'parameter_set',
    data: AssetSearchResultData
  ) => {
    const hasExistingValue = (assetType === 'dataset' && dataset) || (assetType === 'feature_set' && featureSet);

    const needsWarning =
      !hasShownAssetChangeWarning && (assetType === 'feature_set' || assetType === 'dataset') && hasExistingValue;

    if (needsWarning) {
      setPendingAssetChange({ assetType, data });
      setShowAssetChangeWarning(true);
      return;
    }

    await performAssetChange(assetType, data);
  };

  const performAssetChange = async (
    assetType: 'dataset' | 'feature_set' | 'parameter_set',
    data: AssetSearchResultData
  ) => {
    const assetTypeKorean =
      assetType === 'feature_set' ? '피처셋' : assetType === 'dataset' ? '데이터셋' : '파라미터셋';

    try {
      const detail = await fetchAssetDetail({ assetId: data.id.toString(), type: assetType });

      let updatedAssets: WorkbenchAssets = { featureSet, parameterSet, dataset };

      if (assetType === 'dataset' && detail.dataset) {
        const datasetWithTitle = { ...detail.dataset, id: data.id, title: data.title };
        setDataset(datasetWithTitle);
        setHasChangedDataset(true);
        updatedAssets = { ...updatedAssets, dataset: datasetWithTitle };
      } else if (assetType === 'feature_set' && detail.featureSet) {
        const featureSetWithTitle = { ...detail.featureSet, id: data.id, title: data.title };
        setFeatureSet(featureSetWithTitle);
        setHasChangedFeatureSet(true);
        updatedAssets = { ...updatedAssets, featureSet: featureSetWithTitle };
      } else if (assetType === 'parameter_set' && detail.parameterSet) {
        const parameterSetWithTitle = { ...detail.parameterSet, id: data.id, title: data.title };
        setParameterSet(parameterSetWithTitle);
        setHasChangedParameterSet(true);
        updatedAssets = { ...updatedAssets, parameterSet: parameterSetWithTitle };
      }

      if (onAssetChange) {
        onAssetChange(updatedAssets);
      }

      message.success(`${assetTypeKorean}이 정상 변경되었습니다.`);
    } catch (error) {
      console.error(`Failed to fetch ${assetType} detail:`, error);
      notification.error({
        message: `${assetTypeKorean} 변경 실패`,
        description: `${assetTypeKorean} 변경이 실패했습니다.`,
        duration: NOTIFICATION_CONFIG.DURATION.ERROR,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
    }
  };

  const handleConfirmAssetChange = async () => {
    setHasShownAssetChangeWarning(true);
    setShowAssetChangeWarning(false);

    if (pendingAssetChange) {
      await performAssetChange(pendingAssetChange.assetType, pendingAssetChange.data);
      setPendingAssetChange(null);
    }
  };

  const handleCancelAssetChange = () => {
    setShowAssetChangeWarning(false);
    setPendingAssetChange(null);
  };

  const handleAssetClear = (assetType: 'dataset' | 'feature_set' | 'parameter_set') => {
    let updatedAssets: WorkbenchAssets = { featureSet, parameterSet, dataset };

    if (assetType === 'dataset') {
      setDataset(undefined);
      updatedAssets = { ...updatedAssets, dataset: undefined };
    } else if (assetType === 'feature_set') {
      setFeatureSet(undefined);
      updatedAssets = { ...updatedAssets, featureSet: undefined };
    } else if (assetType === 'parameter_set') {
      setParameterSet(undefined);
      updatedAssets = { ...updatedAssets, parameterSet: undefined };
    }

    if (onAssetChange) {
      onAssetChange(updatedAssets);
    }
  };

  const tagBadgeItems = useMemo<BadgeItem[]>(() => {
    const sortedTags = sortTagsSafe(experimentTags);
    return sortedTags.map((tag) => ({
      tagFQN: tag,
      name: tag,
    }));
  }, [experimentTags]);

  const renderTagBadgeList = useCallback(() => {
    return <BadgeList items={tagBadgeItems} showIcon isEllipsis showTooltip emptyText={EMPTY_MESSAGES.NO_TAGS} />;
  }, [tagBadgeItems]);

  const updateTagsMutation = useMutation({
    mutationFn: async (newTags: string[]) => {
      if (!runId) {
        throw new Error('Run ID가 없습니다.');
      }
      await updateRunTags(runId, experimentTags || [], newTags);
    },
    onSuccess: () => {
      message.success('태그가 성공적으로 업데이트되었습니다.');
      setIsEditingTags(false);
      if (runId) {
        queryClient.invalidateQueries({ queryKey: experimentKeys.assetDetail(String(runId), 'run', runId) });
      }
    },
    onError: (error) => {
      console.error('태그 업데이트 실패:', error);
    },
  });

  const handleTagSubmit = useCallback(
    (newTags: string[]) => {
      updateTagsMutation.mutate(newTags);
    },
    [updateTagsMutation]
  );

  return (
    <div className={styles.sidebar}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>데이터셋</div>
        <InfiniteAssetSelect
          assetType="dataset"
          placeholder="데이터셋을 선택하세요"
          value={dataset?.title || dataset?.table}
          onSelectAsset={(data) => handleAssetChange('dataset', data)}
          onChange={(value) => {
            if (!value) {
              handleAssetClear('dataset');
            }
          }}
          initialOption={
            !hasChangedDataset && dataset
              ? {
                  id: dataset.id,
                  title: dataset.title || dataset.table,
                  description: dataset.description || undefined,
                  runId: dataset.runId ?? undefined,
                  type: 'dataset',
                }
              : undefined
          }
          allowClear
        />
        {dataset ? (
          <div className={styles.assetDetail}>
            <div className={styles.datasetCard}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>소유자</span>
                <div className={styles.infoValue}>
                  <EllipsisText text={dataset.creator || '-'} />
                </div>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>생성일</span>
                <div className={styles.infoValue}>
                  <EllipsisText text={dayjs(dataset.createdAt).format('YYYY-MM-DD HH:mm:ss')} />
                </div>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>수정일</span>
                <div className={styles.infoValue}>
                  <EllipsisText text={dayjs(dataset.updatedAt).format('YYYY-MM-DD HH:mm:ss')} />
                </div>
              </div>
              <div className={styles.divider} />
              <div className={styles.columnsTitle}>컬럼</div>
              <BadgeList
                variant="column"
                items={dataset.columns.map(
                  (col): BadgeItem => ({
                    name: col.name,
                    tagFQN: col.name,
                  })
                )}
                showIcon={false}
              />
            </div>
          </div>
        ) : (
          <div className={styles.emptyWrapper}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_MESSAGES.NO_DATA} />
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>피처셋</div>
        <InfiniteAssetSelect
          assetType="feature_set"
          placeholder="피처셋을 선택하세요"
          value={featureSet?.title || featureSet?.table}
          onSelectAsset={(data) => handleAssetChange('feature_set', data)}
          onChange={(value) => {
            if (!value) {
              handleAssetClear('feature_set');
            }
          }}
          initialOption={
            !hasChangedFeatureSet && featureSet
              ? {
                  id: featureSet.id,
                  title: featureSet.title || featureSet.table,
                  description: featureSet.description || undefined,
                  runId: featureSet.runId ?? undefined,
                  type: 'feature_set',
                }
              : undefined
          }
          allowClear
        />
        {featureSet ? (
          <div className={styles.assetDetail}>
            <div className={styles.featureSetCard}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>소유자</span>
                <div className={styles.infoValue}>
                  <EllipsisText text={featureSet.creator || '-'} />
                </div>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>생성일</span>
                <div className={styles.infoValue}>
                  <EllipsisText text={dayjs(featureSet.createdAt).format('YYYY-MM-DD HH:mm:ss')} />
                </div>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>수정일</span>
                <div className={styles.infoValue}>
                  <EllipsisText text={dayjs(featureSet.updatedAt).format('YYYY-MM-DD HH:mm:ss')} />
                </div>
              </div>
              <div className={styles.divider} />
              <div className={styles.columnsTitle}>컬럼</div>
              <BadgeList
                variant="column"
                items={featureSet.columns.map(
                  (col): BadgeItem => ({
                    name: col.name,
                    tagFQN: col.name,
                  })
                )}
                showIcon={false}
              />
            </div>
          </div>
        ) : (
          <div className={styles.emptyWrapper}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_MESSAGES.NO_DATA} />
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>파라미터셋</div>
        <InfiniteAssetSelect
          assetType="run_parameter_set"
          placeholder="파라미터셋을 선택하세요"
          value={parameterSet?.title}
          onSelectAsset={(data) => handleAssetChange('parameter_set', data)}
          onChange={(value) => {
            if (!value) {
              handleAssetClear('parameter_set');
            }
          }}
          initialOption={
            !hasChangedParameterSet && parameterSet
              ? {
                  id: parameterSet.id,
                  title: parameterSet.title,
                  description: parameterSet.description || undefined,
                  runId: parameterSet.runId ?? undefined,
                  type: 'run_parameter_set',
                }
              : undefined
          }
          allowClear
        />
        {parameterSet && parameterSet.hyperparameters ? (
          <div className={styles.assetDetail}>
            <div className={styles.parameterSetCard}>
              {Object.entries(parameterSet.hyperparameters).map(([key, value]) => (
                <div key={key} className={styles.infoRow}>
                  <div className={styles.infoLabel}>
                    <EllipsisText text={key} />
                  </div>
                  <div className={styles.infoValue}>
                    <EllipsisText text={String(value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyWrapper}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_MESSAGES.NO_DATA} />
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          태그
          <IconButton size="small" onClick={() => setIsEditingTags(true)} tooltip="태그 편집">
            <Icon name="edit" size={16} />
          </IconButton>
        </div>
        <div className={styles.assetDetail}>{renderTagBadgeList()}</div>
      </div>

      <ConfirmModal
        open={showAssetChangeWarning}
        message={
          <>
            목록에는 최신 버전만 표시됩니다.
            <br />
            이전 선택으로 되돌리기 어려울 수 있습니다.
            <br />
            <br />
            계속하시겠습니까?
          </>
        }
        onConfirm={handleConfirmAssetChange}
        onCancel={handleCancelAssetChange}
      />

      <ExperimentTagsEditModal
        open={isEditingTags}
        tags={experimentTags || []}
        onClose={() => setIsEditingTags(false)}
        onSubmit={handleTagSubmit}
      />
    </div>
  );
};
